import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { billingApi, BroadcastCertificate } from '../services/api';
import '../styles/reports.css';
import '../styles/users.css';
import { useTranslation } from 'react-i18next';
import { currentLocale } from '../i18n';

function formatDate(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
}

function formatNumber(n: number): string {
  return n.toLocaleString('en-US').replace(/,/g, '\u2009');
}

const CertificatesPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'regional_admin';
  const isClient = user?.role === 'client';
  const [certificates, setCertificates] = useState<BroadcastCertificate[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [periodMonth, setPeriodMonth] = useState('');
  const [periodYear, setPeriodYear] = useState('');
  const [officeFilter, setOfficeFilter] = useState('');
  const [ownerQuery, setOwnerQuery] = useState('');
  const [selectedOwner, setSelectedOwner] = useState<{ name: string; type: string } | null>(null);
  const [showOwnerDropdown, setShowOwnerDropdown] = useState(false);
  const ownerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(timer);
  }, [search]);

  const loadCertificates = useCallback(async () => {
    try {
      const data = await billingApi.listCertificates();
      setCertificates(data.certificates);
    } catch (e) {
      console.error('Failed to load certificates', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadCertificates(); }, [loadCertificates]);

  const handleDownloadPdf = async (id: number) => {
    try {
      const { blob, filename } = await billingApi.downloadCertificatePdf(id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      alert(t('billing.errDownloadPdf'));
    }
  };

  // Build unique owners list from certificates
  const ownerOptions = useMemo(() => {
    const map = new Map<string, { name: string; type: string }>();
    certificates.forEach(cert => {
      const ag = (cert as any).agency_name;
      const fl = (cert as any).freelancer_name;
      if (ag) map.set(`agency:${ag}`, { name: ag, type: 'Agency' });
      if (fl) map.set(`freelancer:${fl}`, { name: fl, type: 'Freelancer' });
    });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [certificates]);

  const filteredOwners = ownerQuery
    ? ownerOptions.filter(o => o.name.toLowerCase().includes(ownerQuery.toLowerCase()))
    : ownerOptions;

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ownerRef.current && !ownerRef.current.contains(e.target as Node)) {
        setShowOwnerDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Build unique office list from certificates
  const officeOptions = useMemo(() => {
    const set = new Set<string>();
    certificates.forEach(cert => { if (cert.office_name) set.add(cert.office_name); });
    return Array.from(set).sort();
  }, [certificates]);

  const hasFilters = search || periodMonth || periodYear || officeFilter || selectedOwner;

  const clearFilters = () => {
    setSearch('');
    setPeriodMonth('');
    setPeriodYear('');
    setOfficeFilter('');
    setSelectedOwner(null);
    setOwnerQuery('');
  };

  // Group certificates by advertiser+period into rows with agency/client pair
  const groupedRows = useMemo(() => {
    const map = new Map<string, {
      agency?: BroadcastCertificate;
      client?: BroadcastCertificate;
    }>();
    certificates.forEach(cert => {
      const key = `${cert.advertiser_id}:${cert.period_start}:${cert.period_end}`;
      if (!map.has(key)) map.set(key, {});
      const entry = map.get(key)!;
      if (cert.cert_type === 'client') entry.client = cert;
      else entry.agency = cert;
    });
    return Array.from(map.values()).map(pair => ({
      cert: pair.agency || pair.client!,
      agencyId: pair.agency?.id,
      clientId: pair.client?.id,
    }));
  }, [certificates]);

  const filtered = groupedRows.filter(row => {
    const cert = row.cert;
    if (periodYear && cert.period_start && !cert.period_start.startsWith(periodYear)) return false;
    if (periodMonth && cert.period_start) {
      const m = cert.period_start.substring(5, 7);
      if (m !== periodMonth) return false;
    }
    if (officeFilter && cert.office_name !== officeFilter) return false;
    if (selectedOwner) {
      const ag = (cert as any).agency_name;
      const fl = (cert as any).freelancer_name;
      if (selectedOwner.type === 'Agency' && ag !== selectedOwner.name) return false;
      if (selectedOwner.type === 'Freelancer' && fl !== selectedOwner.name) return false;
    }
    if (!debouncedSearch) return true;
    const q = debouncedSearch.toLowerCase();
    return (
      cert.advertiser_name?.toLowerCase().includes(q) ||
      cert.advertiser_id?.toLowerCase().includes(q)
    );
  });

  return (
    <>
      <div className="header">
        <div className="header-title">
          <h1>{t('certs.title')}</h1>
          <p className="header-subtitle">{t('certs.subtitle')}</p>
        </div>
      </div>

      <div className="reports-layout">
        <aside className="filters-panel">
          <div className="filter-section">
            <h3 className="section-heading">{t('common.search')}</h3>
            <div className="campaign-search-input-wrapper">
              <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
              <input
                type="text"
                className="campaign-search-input"
                placeholder={t('certs.searchAdvertiser')}
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
          </div>

          {isAdmin && (
            <div className="filter-section">
              <h3 className="section-heading">{t('clients.regionalOffice')}</h3>
              <select
                className="form-select"
                value={officeFilter}
                onChange={e => setOfficeFilter(e.target.value)}
              >
                <option value="">{t('clients.allOffices')}</option>
                {officeOptions.map(o => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            </div>
          )}

          <div className="filter-section">
            <h3 className="section-heading">{t('billing.colPeriod')}</h3>
            <div style={{ display: 'flex', gap: '6px' }}>
              <select
                className="form-select"
                style={{ flex: 3, minWidth: 0 }}
                value={periodMonth}
                onChange={e => setPeriodMonth(e.target.value)}
              >
                <option value="">{t('billing.month')}</option>
                {/* Month names come from Intl; the value stays the month number. */}
                {Array.from({ length: 12 }, (_, i) => {
                  const name = new Date(2000, i, 1).toLocaleDateString(currentLocale(), { month: 'long' });
                  return (
                    <option key={i} value={String(i + 1).padStart(2, '0')}>
                      {name.charAt(0).toUpperCase() + name.slice(1)}
                    </option>
                  );
                })}
              </select>
              <select
                className="form-select"
                style={{ flex: 2, minWidth: 0 }}
                value={periodYear}
                onChange={e => setPeriodYear(e.target.value)}
              >
                <option value="">{t('billing.year')}</option>
                {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(y => (
                  <option key={y} value={String(y)}>{y}</option>
                ))}
              </select>
            </div>
          </div>

          {isAdmin && (
            <div className="filter-section">
              <h3 className="section-heading">{t('billing.owner')}</h3>
              <div className="campaign-search-container" ref={ownerRef}>
              <div className="campaign-search-input-wrapper">
                <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8"></circle>
                  <path d="m21 21-4.35-4.35"></path>
                </svg>
                <input
                  type="text"
                  className="campaign-search-input"
                  placeholder={t('billing.phOwner')}
                  value={selectedOwner ? selectedOwner.name : ownerQuery}
                  onChange={e => {
                    setOwnerQuery(e.target.value);
                    setSelectedOwner(null);
                    setShowOwnerDropdown(true);
                  }}
                  onFocus={() => setShowOwnerDropdown(true)}
                />
                {selectedOwner && (
                  <button
                    type="button"
                    className="search-clear-btn"
                    onClick={() => { setSelectedOwner(null); setOwnerQuery(''); }}
                    title={t('billing.clear')}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M18 6L6 18M6 6l12 12"></path>
                    </svg>
                  </button>
                )}
              </div>
              {showOwnerDropdown && !selectedOwner && filteredOwners.length > 0 && (
                <div className="campaign-search-dropdown">
                  {filteredOwners.map(o => (
                    <div
                      key={`${o.type}:${o.name}`}
                      className="campaign-search-item"
                      onClick={() => {
                        setSelectedOwner(o);
                        setOwnerQuery('');
                        setShowOwnerDropdown(false);
                      }}
                    >
                      <div className="campaign-search-item-name">{o.name}</div>
                      <div className="campaign-search-item-advertiser">
                        {t(o.type === 'Agency' ? 'billing.ownerAgency' : 'billing.ownerFreelancer')}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          )}

          {hasFilters && (
            <div className="filter-section" style={{ marginTop: '8px' }}>
              <button className="btn-secondary" style={{ width: '100%' }} onClick={clearFilters}>
                {t('billing.clearFilters')}
              </button>
            </div>
          )}

        </aside>

        <div className="results-area visible">
          {loading ? (
            <div className="users-loading">
              <div className="users-spinner" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="editor-empty">
              <div className="editor-empty-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2">
                  <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6z" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M14 2v6h6" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M9 15l2 2 4-4" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>{t('certs.noneFound')}</h3>
              <p>{t(hasFilters ? 'billing.tryAdjusting' : 'certs.generatedAuto')}</p>
            </div>
          ) : (
            <div className="users-table-container">
              <table className="users-table">
                <thead>
                  <tr>
                    <th>{t('certs.colAdvertiser')}</th>
                    <th style={{ width: '160px' }}>{t('billing.colPeriod')}</th>
                    <th style={{ width: '120px', textAlign: 'right' }}>{t('certs.colImpressions')}</th>
                    <th style={{ width: '100px', textAlign: 'right' }}>{t('certs.colClicks')}</th>
                    <th style={{ width: '100px' }}>{t('certs.colCreated')}</th>
                    <th style={{ width: '160px' }}>PDF</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(row => {
                    const cert = row.cert;
                    return (
                      <tr key={`${cert.advertiser_id}:${cert.period_start}`}>
                        <td>
                          <span className="users-user-name" style={{ fontSize: '13px' }}>
                            {cert.advertiser_name || cert.advertiser_id}
                          </span>
                          <span className="users-user-email" style={{ display: 'block' }}>
                            {cert.office_name || ''}
                            {(cert as any).agency_name && (
                              <span className="clients-agency-badge">{(cert as any).agency_name}</span>
                            )}
                            {(cert as any).freelancer_name && (
                              <span className="clients-agency-badge" style={{ background: '#e8f5e9', color: '#2e7d32' }}>{(cert as any).freelancer_name}</span>
                            )}
                          </span>
                        </td>
                        <td style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                          {cert.period_start
                            ? new Date(cert.period_start + 'T00:00').toLocaleDateString(currentLocale(), { month: 'long', year: 'numeric' })
                            : '\u2014'}
                        </td>
                        <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontSize: '14px', fontWeight: 500 }}>
                          {formatNumber(cert.total_impressions)}
                        </td>
                        <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontSize: '14px', fontWeight: 500 }}>
                          {formatNumber(cert.total_clicks)}
                        </td>
                        <td style={{ fontSize: '13px', whiteSpace: 'nowrap' }}>
                          {cert.created_at ? formatDate(cert.created_at) : ''}
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: '4px' }}>
                            {row.agencyId && !isClient && (
                              <button
                                className="btn-secondary"
                                onClick={() => handleDownloadPdf(row.agencyId!)}
                                title={t('certs.downloadAgencyPdf')}
                                style={{ padding: '4px 8px', fontSize: '12px' }}
                              >
                                {t('certs.agency')}
                              </button>
                            )}
                            {row.clientId && (
                              <button
                                className="btn-secondary"
                                onClick={() => handleDownloadPdf(row.clientId!)}
                                title={t('billing.downloadPdf')}
                                style={{ padding: '4px 8px', fontSize: '12px' }}
                              >
                                {isClient ? 'PDF' : t('certs.client')}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CertificatesPage;
